import { useEffect, useMemo, useRef, useState } from "react";
import client from "../../lib/api";
import {
  Sparkles, Send, Plus, Trash2, MessageSquare, Save, Loader2, BookOpen, X,
} from "lucide-react";

/*
 * Victoria — TheSaiyanVictoria's private brand strategist.
 *
 * Full-page workspace: brand-brief editor (left), chat (center), conversation
 * history (right). Auto-routes each message to the strongest model per
 * question type (Claude for brand, Gemini for data, GPT for general).
 */

export default function VictoriaPage() {
  const [convs, setConvs] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [brief, setBrief] = useState("");
  const [briefSaving, setBriefSaving] = useState(false);
  const [briefEditorOpen, setBriefEditorOpen] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => { refreshList(); loadBrief(); }, []);
  useEffect(() => { if (activeId) loadConv(activeId); else setMessages([]); }, [activeId]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, sending]);

  async function refreshList() {
    const r = await client.get("/admin/victoria/conversations");
    setConvs(r.data || []);
  }
  async function loadBrief() {
    const r = await client.get("/admin/victoria/brand-brief");
    setBrief(r.data?.brief || "");
  }
  async function loadConv(id) {
    const r = await client.get(`/admin/victoria/conversations/${id}`);
    setMessages(r.data?.messages || []);
  }
  async function saveBrief() {
    setBriefSaving(true);
    try {
      await client.put("/admin/victoria/brand-brief", { brief });
    } finally {
      setBriefSaving(false);
    }
  }
  async function newChat() {
    setActiveId(null);
    setMessages([]);
    setDraft("");
  }
  async function deleteConv(id) {
    if (!confirm("Delete this conversation? This can't be undone.")) return;
    await client.delete(`/admin/victoria/conversations/${id}`);
    if (activeId === id) setActiveId(null);
    refreshList();
  }
  async function send() {
    if (!draft.trim() || sending) return;
    const text = draft;
    setDraft("");
    setSending(true);
    // Optimistic user echo
    const optimistic = { id: `local-${Date.now()}`, role: "user", content: text, created_at: new Date().toISOString() };
    setMessages((m) => [...m, optimistic]);
    try {
      const r = await client.post("/admin/victoria/chat", {
        message: text,
        conversation_id: activeId,
      });
      const { conversation_id, user_message, assistant_message } = r.data;
      setMessages((m) => {
        const withoutOptim = m.filter((x) => x.id !== optimistic.id);
        return [...withoutOptim, user_message, assistant_message];
      });
      if (conversation_id !== activeId) setActiveId(conversation_id);
      refreshList();
    } catch (e) {
      setMessages((m) => [...m, {
        id: `err-${Date.now()}`,
        role: "assistant",
        content: `Victoria upstream error: ${e?.response?.data?.detail || e?.message || "unknown"}. Try again in a moment.`,
        error: true,
        created_at: new Date().toISOString(),
      }]);
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="space-y-4" data-testid="victoria-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-full bg-gradient-to-br from-[#FF6B00] to-[#FFB800] flex items-center justify-center aura-pulse">
            <Sparkles className="w-5 h-5 text-black" />
          </div>
          <div>
            <span className="text-[10px] uppercase tracking-[0.3em] text-[#FFB800]">Admin AI</span>
            <h1 className="text-3xl font-black text-white tracking-tight">Victoria</h1>
            <p className="text-xs text-zinc-500">Private brand strategist · reads live metrics · Claude / GPT / Gemini auto-routed.</p>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            data-testid="brief-toggle"
            onClick={() => setBriefEditorOpen(!briefEditorOpen)}
            className="inline-flex items-center gap-2 h-10 px-4 rounded-full bg-zinc-900/60 border border-white/10 hover:border-[#FFB800]/50 text-xs uppercase tracking-widest text-white"
          >
            <BookOpen className="w-4 h-4" /> Brand Brief
          </button>
          <button
            data-testid="victoria-new-chat"
            onClick={newChat}
            className="inline-flex items-center gap-2 h-10 px-4 rounded-full bg-[#FF6B00] hover:bg-[#FF6B00]/90 text-black text-xs uppercase tracking-widest font-bold"
          >
            <Plus className="w-4 h-4" /> New Chat
          </button>
        </div>
      </div>

      {/* Brand-brief drawer */}
      {briefEditorOpen && (
        <div className="rounded-xl bg-zinc-900/60 border border-[#FFB800]/30 p-5" data-testid="brief-editor">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-xs uppercase tracking-[0.3em] text-[#FFB800] font-bold">Brand Brief · Victoria&apos;s ground truth</h3>
            <button onClick={() => setBriefEditorOpen(false)} className="text-zinc-500 hover:text-white"><X className="w-4 h-4" /></button>
          </div>
          <textarea
            value={brief}
            onChange={(e) => setBrief(e.target.value)}
            data-testid="brief-textarea"
            className="w-full h-40 rounded-lg bg-black/60 border border-white/10 p-3 text-sm text-white font-mono resize-none focus:outline-none focus:border-[#FF6B00]/60"
            placeholder="Describe your voice, cadence, do's and don'ts. Victoria uses this on every reply."
          />
          <div className="flex justify-end mt-3">
            <button
              data-testid="brief-save"
              onClick={saveBrief}
              disabled={briefSaving}
              className="inline-flex items-center gap-2 h-9 px-4 rounded-full bg-[#FFB800] hover:bg-[#FFB800]/90 text-black text-xs uppercase tracking-widest font-bold disabled:opacity-50"
            >
              {briefSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Save Brief
            </button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[1fr,260px] gap-4">
        {/* Chat panel */}
        <div className="rounded-xl bg-zinc-900/60 border border-white/5 flex flex-col min-h-[520px] overflow-hidden" data-testid="chat-panel">
          <div className="flex-1 overflow-y-auto p-5 space-y-4">
            {messages.length === 0 && !sending && (
              <StarterPrompts onPick={(t) => setDraft(t)} />
            )}
            {messages.map((m) => <MessageBubble key={m.id} msg={m} />)}
            {sending && <TypingIndicator />}
            <div ref={bottomRef} />
          </div>
          <div className="border-t border-white/5 p-3 bg-black/30">
            <div className="flex gap-2 items-end">
              <textarea
                data-testid="victoria-input"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
                }}
                rows={2}
                placeholder="Ask Victoria… (Enter to send · Shift+Enter for newline · @claude / @gpt / @gemini to force a model)"
                className="flex-1 rounded-lg bg-black/60 border border-white/10 p-3 text-sm text-white resize-none focus:outline-none focus:border-[#FF6B00]/60"
              />
              <button
                data-testid="victoria-send"
                onClick={send}
                disabled={!draft.trim() || sending}
                className="h-11 px-5 rounded-lg bg-[#FF6B00] hover:bg-[#FF6B00]/90 text-black font-bold disabled:opacity-40 inline-flex items-center gap-2"
              >
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                Send
              </button>
            </div>
          </div>
        </div>

        {/* Conversation history sidebar */}
        <aside className="rounded-xl bg-zinc-900/60 border border-white/5 p-3 h-full" data-testid="conv-history">
          <h3 className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 px-2 py-2">Recent Chats</h3>
          {convs.length === 0 ? (
            <p className="text-xs text-zinc-500 px-2 py-3">No conversations yet.</p>
          ) : (
            <ul className="space-y-1">
              {convs.map((c) => (
                <li key={c.id}>
                  <button
                    data-testid={`conv-${c.id}`}
                    onClick={() => setActiveId(c.id)}
                    className={`w-full text-left px-2.5 py-2 rounded-lg text-xs group flex items-start gap-2 ${
                      activeId === c.id ? "bg-[#FF6B00]/15 text-[#FFB800] border border-[#FF6B00]/30" : "text-zinc-400 hover:text-white hover:bg-white/5 border border-transparent"
                    }`}
                  >
                    <MessageSquare className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                    <span className="flex-1 truncate">{c.title || "Untitled"}</span>
                    <button
                      onClick={(e) => { e.stopPropagation(); deleteConv(c.id); }}
                      className="opacity-0 group-hover:opacity-100 text-zinc-500 hover:text-red-400 transition-opacity"
                      data-testid={`conv-delete-${c.id}`}
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </aside>
      </div>
    </div>
  );
}

function StarterPrompts({ onPick }) {
  const starters = [
    { emoji: "📈", label: "What's my #1 traffic channel this month and how do I 2× it?" },
    { emoji: "🎬", label: "Pitch me three POV series concepts that fit my Saiyan aesthetic." },
    { emoji: "⏰", label: "When should I post my next drop for peak engagement?" },
    { emoji: "🎯", label: "Which video has the worst retention and how do I fix the hook?" },
    { emoji: "🎨", label: "Give me 5 alternate titles for my top video." },
  ];
  return (
    <div className="max-w-2xl mx-auto py-8" data-testid="starter-prompts">
      <p className="text-center text-zinc-500 text-sm mb-6">Ask anything about your brand or metrics.</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {starters.map((s, i) => (
          <button
            key={i}
            onClick={() => onPick(s.label)}
            data-testid={`starter-${i}`}
            className="text-left px-4 py-3 rounded-xl bg-black/40 border border-white/10 hover:border-[#FFB800]/40 text-sm text-zinc-300 hover:text-white"
          >
            <span className="mr-2">{s.emoji}</span>{s.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function MessageBubble({ msg }) {
  const isUser = msg.role === "user";
  const providerBadge = useMemo(() => {
    if (isUser || !msg.model) return null;
    const [p] = String(msg.model).split("/");
    return p;
  }, [msg, isUser]);
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`} data-testid={`msg-${msg.role}`}>
      <div className={`max-w-[85%] rounded-2xl px-4 py-3 ${
        isUser
          ? "bg-[#FF6B00] text-black rounded-br-sm"
          : msg.error
            ? "bg-red-900/40 border border-red-500/40 text-red-200 rounded-bl-sm"
            : "bg-black/40 border border-white/10 text-zinc-100 rounded-bl-sm"
      }`}>
        {!isUser && providerBadge && (
          <div className="mb-2 text-[9px] uppercase tracking-widest text-[#FFB800] flex items-center gap-1">
            <Sparkles className="w-3 h-3" /> Victoria · {providerBadge}
            {msg.route_reason && <span className="text-zinc-500 normal-case tracking-normal ml-1">({msg.route_reason})</span>}
          </div>
        )}
        <div className="text-sm whitespace-pre-wrap leading-relaxed">{msg.content}</div>
      </div>
    </div>
  );
}

function TypingIndicator() {
  return (
    <div className="flex justify-start" data-testid="typing">
      <div className="max-w-[80%] rounded-2xl px-4 py-3 bg-black/40 border border-white/10 text-zinc-400 rounded-bl-sm">
        <div className="flex items-center gap-2 text-xs">
          <Sparkles className="w-3.5 h-3.5 text-[#FFB800] animate-pulse" />
          <span className="text-[#FFB800]">Victoria is thinking…</span>
        </div>
        <div className="flex gap-1 mt-2">
          <span className="w-2 h-2 rounded-full bg-[#FF6B00] animate-bounce" style={{ animationDelay: "0ms" }} />
          <span className="w-2 h-2 rounded-full bg-[#FF6B00] animate-bounce" style={{ animationDelay: "150ms" }} />
          <span className="w-2 h-2 rounded-full bg-[#FF6B00] animate-bounce" style={{ animationDelay: "300ms" }} />
        </div>
      </div>
    </div>
  );
}
